<template>
  <div class="chat-section">
    <div class="cs-wrapper clearfixing" v-if="currentConversation">
      <!-- Header -->
      <div class="cs-header">
        <div class="cs-header-name">{{ currentConversation.fullName }}</div>
        <div class="cs-time-info" v-show="isChatDate">
          <span class="chatDate" ref="chatDate"></span>
        </div>
      </div>
      <!-- Chat area -->
      <div
        class="cs-messages-area"
        @scroll="scrollApiCall"
        ref="chatArea"
        :style="{height: detectMobile ? isIOS(221): ''}"
      >
        <div class="cs-messages-container" ref="chatAreaContainer">
          <!-- Read messages -->
          <div
            class="clearfixing cs-read-messages"
            v-if="messages.listOfReadMessages.length > 0"
            ref="readMessagesArea"
          >
            <div
              class="conversation-box clearfixing"
              v-for="(message, index) in messages.listOfReadMessages"
              :key="index"
              :data-date="formatDateTime(message.createdAt)"
            >
              <div
                v-if="currentConversation.participantGuid !== message.senderGuid"
                class="cs-msg-box-me"
              >
                <div class="cs-msg-box-me-time">{{ formatDateTime(message.createdAt)}}</div>
                <div class="cs-msg-box-me-msg"> {{ message.messageBody }}</div>
              </div>

              <div class="cs-msg-box-other" v-else>
                <div class="cs-msg-box-other-info">
                  <span
                    class="cs-msg-box-other-name"
                    :style="{ color: currentConversation.color }"
                  >
                    {{ currentConversation.fullName }}
                  </span>
                  <span class="cs-msg-box-other-time">{{ formatDateTime(message.createdAt) }}</span>
                </div>
                <div class="cs-msg-box-other-msg" :style="{ backgroundColor: currentConversation.color }">{{ message.messageBody }}</div>
              </div>
            </div>
          </div>

          <!-- Separator -->
          <div
            v-if="messages.listOfUnreadMessages.length > 0"
            class="new-msg-separator"
            :class="{ 'hidden': isNewMsgSeparatorHidden }"
          >
            <span>{{ $lang.messages.newMessages }}</span>
          </div>

          <!-- Unread messages -->
          <div
            class="clearfixing cs-unread-messages"
            v-if="messages.listOfUnreadMessages.length > 0"
            ref="unreadMessagesArea"
          >
            <div
              class="conversation-box clearfixing"
              v-for="(message, index) in messages.listOfUnreadMessages"
              :key="index"
              :data-date="formatDateTime(message.createdAt)"
              :ref="message.createdAt"
            >
              <div
                v-if="currentConversation.participantGuid !== message.senderGuid"
                class="cs-msg-box-me"
              >
                <div class="cs-msg-box-me-time">{{ formatDateTime(message.createdAt) }}</div>
                <div class="cs-msg-box-me-msg"> {{ message.messageBody }}</div>
              </div>

              <div
                v-else
                class="cs-msg-box-other"
              >
                <div class="cs-msg-box-other-info">
                  <span
                    class="cs-msg-box-other-name"
                    :style="{ color: currentConversation.color }"
                  >
                    {{ currentConversation.fullName }}
                  </span>
                  <span class="cs-msg-box-other-time">{{ formatDateTime(message.createdAt) }}</span>
                </div>
                <div class="cs-msg-box-other-msg" :style="{ backgroundColor: currentConversation.color }">
                  {{ message.messageBody }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="go-to-bottom"
        @click="jumpToBottom"
        ref="goToBottom"
        :class="{'show-bottom-btn': showHideBottomBtn}">
      </div>

      <!-- Send message area -->
      <div class="cs-sendMsg-area">
        <input type="hidden" class="cs-sendMsg-touched">
        <div class="cs-sendMsg-wrapper">
          
          <a class="cs-sendMsg-emoticon">
            <span
              class="spirit-ico"
              :class="{'arrow' : spiritTooltip }"
              @mouseover="showSpiritTooltip"
              @click="showSpiritTooltip"
            >
              <img
                v-show="nlp === 'positive'"
                src="@/assets/images/smiley_positive_ico.png"
                alt="emotico"
              >
              <img
                v-show="nlp === 'neutral'"
                src="@/assets/images/smiley_neutral_ico.png"
                alt="emotico"
              >
              <img
                v-show="nlp === 'mixed'"
                src="@/assets/images/smiley_mixed_ico.png"
                alt="emotico"
              >
              <img
                v-show="nlp === 'negative'"
                src="@/assets/images/smiley_negative_ico.png"
                alt="emotico"
              >
            </span>
  
            <f-p-tooltip
              v-show="spiritTooltip"
              v-click-outside="hideSpiritTooltip"
              :hideTooltipInside="hideSpiritTooltip"
              :tooltipTitle="$lang.messages.tp_spiritTitle"
              :tooltipContent="$lang.messages.tp_spiritText"
              :tooltipHasLink="''">
            </f-p-tooltip>
          </a>
          
          <div class="cs-sendMsg-text">
            <textarea
              name="message"
              :placeholder="$lang.messages.msgTxtPlaceholder"
              v-model="MX_newMsg"
              @keydown.enter.exact.prevent
              @keyup.enter.exact="sendNewMessageData"
              @keydown.space="checkNLP"
              @paste="postNlpOnPaste"
              maxlength="500"
              cols=""
              rows="1">
            </textarea>
          </div>
          <div class="cs-sendMsg-btn-area">
            <a class="cs-sendMsg-btn-text" @click="sendNewMessageData">
              <span>{{ $lang.messages.sendMessage }}</span>
            </a>
          </div>

          <!-- Confirm Modal -->
          <n-l-p-confirm-modal
            ref="confirmModalComponent"
            @confirmMessage="confirmMessage">
          </n-l-p-confirm-modal>
        </div>
        <span class="textarea-counter" :class="{'textarea-counter--blink': MX_newMsg.length >= 500}">
          {{MX_newMsg.length >= 500 ? 500 : MX_newMsg.length}} / 500
        </span>

      </div>
    </div>

    <div class="cs-no-wrapper-background" v-else></div>
  </div>
</template>

<script>
  import {mapActions, mapGetters} from 'vuex';
  import {GET_CLICKED_ON_CONTACT, POST_NLP_SCORE} from '@/store/action.types';
  import NLPConfirmModal from "@/components/common/modals/NLPConfirmModal";
  import {format, subDays} from 'date-fns';
  import {checkSentiment} from "@/mixins/checkSentiment";
  import {checkSentimentOnPaste} from "@/mixins/checkSentimentOnPaste";
  import {isIOS} from '@/mixins/isIOS';
  import vClickOutside from 'v-click-outside';


  export default {
    components: {
      NLPConfirmModal,
      'f-p-tooltip': () => import('@/components/common/FPTooltip')
    },
    mixins: [
      checkSentiment,
      checkSentimentOnPaste,
      isIOS
    ],
    directives: {
      clickOutside: vClickOutside.directive
    },
    data() {
      return {
        firstLoad: false,
        // messageCountUp: 0,
        // messageCountDown: 0,
        nlp: 'positive',
        newMessageSent: false,
        initialMsgContainerHeight: 0,
        initialUnreadMsgCont: 0,
        showHideBottomBtn: false,
        confirm: true,
        text: null,
        isChatDate: false,
        scrollerPositionDown: false,
        scrollerPositionUp: false,
        goToBottom: false,
        jump: false,
        messages: {
          listOfReadMessages: [],
          listOfUnreadMessages: []
        },
        initScrollTop: 0,
        lastScrollTop:0,
        isClickedOnNewMsgBtn: false,
        isNewMsgSeparatorHidden: true,
        spiritTooltip: false
      }
    },
    computed: {
      ...mapGetters(["getHasClickedOnContact", "coparentGuid","getScrollDirection"])
    },
    props: {
      currentConversation: {
        type: Object,
        required: true,
        default: () => {}
      },
      detectMobile: {
        type: Boolean,
        required: true,
        default: () => false,
      }
    },
    beforeMount() {
      // We use this so we could, on first load of component,
      // put the chat area scroll area to bottom
      this.firstLoad = true;
    },
    mounted() {
      // Show the first 15 messages
      this.$root.$on('messages', this.messagesUpdated);
    },
    updated() {
      // this.setScroller();
      // this.showToBottomBtn();

      // On initial load of component scroll to the bottom of chat area div,
      // also when new message arrives also scroll to bottom
      if (this.firstLoad || this.isClickedOnNewMsgBtn || this.getHasClickedOnContact) {
        this.initScrollChatArea();
        this.initScrollTop = 0;
        this.setChatAreaScrollPosOnUnreadMsg();
      }
    },
    methods: {
      ...mapActions({
        postNlpScore: POST_NLP_SCORE,
        GET_CLICKED_ON_CONTACT
      }),
      messagesUpdated(messages) {
        this.messages = messages
        //when the user scrolls to the top of the chat, we want to load the previous messages at the top of the chat
        //after previous messages get loaded we want subsequent to be rendered at the bottom of the chat
        if(this.getScrollDirection==="UP"){
          this.$store.dispatch('GET_SCROLL_DIRECTION', 'DOWN');
        }
      },
      // Check for conditions inside sentiment analysis
      checkNLP() {
        this.MX_checkNLP()
      },
      // Call sentiment analysis when user type the text into textarea
      postNLP() {
        this.MX_postNLP()
      },
      // Call sentiment analysis when user copy/paste the text into textarea
      postNlpOnPaste (e) {
        this.text = e.clipboardData.getData('text');
        this.MX_postNlpOnPaste(this.text, e)
      },
      confirmModalOpen() {
        this.$refs.confirmModalComponent.show(this.MX_newMsg);
      },
      confirmMessage() {
        this.confirm = true;
        this.nlp = 'neutral';
        this.sendNewMessageData();
      },
      initScrollChatArea () {
        let chatAreaRefs = this.$refs.chatArea;
        chatAreaRefs.scrollTop = chatAreaRefs.scrollHeight;
        this.showHideBottomBtn = false;
      },
      // e.target.scrollHeight - height of element including non-visible content
      // e.target.scrollTop    - number of px that an element content is scrolled vertically
      // e.target.clientHeight - height of element with padding, but NOT border, margin or scrollbar
      // e.target.offsetHeight - height of element with padding, border and it's scrollbar
      scrollApiCall (e) {
        // Return state to default
        this.isClickedOnNewMsgBtn = false;
        this.firstLoad = false;

        // Set the current time or date on time info <span> when user scroll through chat area
        this.setDateOnScroll();

        //scrolling is negative, so the bottom of the chat has a scrollTop value of 0
        //and the top of the chat has a scrollTop value of clientHeight - scrollHeight
        if(this.lastScrollTop < e.target.scrollTop && this.getScrollDirection!=='DOWN'){
          this.$store.dispatch('GET_SCROLL_DIRECTION', 'DOWN');
        }
        this.lastScrollTop = e.target.scrollTop;

        // When we hit the top/bottom of chat area:
        // - send the scroll position to parent ( MessageCenter.vue )
        // - get new messages on scroll "UP" or "DOWN"

        // "UP"
        if (e.target.scrollHeight + e.target.scrollTop === e.target.clientHeight) {
          this.$store.dispatch('GET_SCROLL_DIRECTION', 'UP');
          setTimeout(() => this.loadSubsequentMessages('UP'), 1);
        }
        // "DOWN"
        else if (e.target.scrollTop === 0) {
           this.$store.dispatch('GET_SCROLL_DIRECTION', 'DOWN')
        }

        // When we switch between contacts, reset the store to default
        // and don't do any calculations on scroll
        if (this.getHasClickedOnContact) {
          this.$store.dispatch('GET_CLICKED_ON_CONTACT', false);
          return
        }

        // Set the show/hide of go to bottom button
        this.initGoToBottomBtn(e);
      },
      // When we have new unread messages, scroll the chat area to that position
      // etc. "new message" separator - 50px
      // Show the new message separator <div>
      setChatAreaScrollPosOnUnreadMsg () {
        if (this.messages.listOfUnreadMessages.length) {
          let unreadMsgSeparate = this.$refs.unreadMessagesArea;
          let chatAreaRefs = this.$refs.chatArea;

          this.isNewMsgSeparatorHidden = false;
          unreadMsgSeparate.scrollIntoView();
          chatAreaRefs.scrollTop -= 50;
        }
      },
      initGoToBottomBtn (e) {
        // Grab the current value of chat area scroll position
        let currentScroll = e.target.scrollTop;

        // Show go to bottom button when we scroll UP, or remove it when we scrolled the chat area <div> to the bottom
        // Signal the Message Center about our position ( "UP" or "DOWN" )
        // When we are at the bottom of the chat area <div>, hide the new message separator
        if (currentScroll < this.initScrollTop) {
          this.showHideBottomBtn = true;
        }
        // When we hit bottom of chat area:
        // - hide go to bottom button
        // - hide new message separator
        else if (e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight) {
          this.showHideBottomBtn = false;
          this.isNewMsgSeparatorHidden = true;
        }

        // Set the postion of initialScrollTop to be in sync with current scroll position of chat area
        this.initScrollTop = currentScroll <= 0 ? 0 : currentScroll;
      },
      // When we click on go to bottom button:
      // - scroll to bottom
      // - reset scrollDirection
      // - hide new message separator
      jumpToBottom () {
        this.initScrollChatArea();
        this.$emit('loadAllUnreadMessages');
        this.$store.dispatch('GET_SCROLL_DIRECTION', '');
        this.isNewMsgSeparatorHidden = true;
      },
      loadSubsequentMessages(scrollDirection) {
        this.$emit('loadSubsequentMessages', this.getMessageTimestamp(scrollDirection), scrollDirection)
      },
      // Set time or date in the top of conversation area
      setDateOnScroll() {
        // When we switch to contact with whome we don't have any conversations
        // don't show the chat info date/time
        if (this.getHasClickedOnContact && !this.currentConversation.lastMessagePreview) {
          this.isChatDate = false;
          return
        }

        let viewportTop = this.$refs.chatArea.getBoundingClientRect().top;
        let allBoxes = document.getElementsByClassName('conversation-box');

        for (let el of allBoxes) {
          let elementTop = el.getBoundingClientRect().top;
          let elementBottom = el.getBoundingClientRect().bottom;

          if (elementTop >= viewportTop || elementBottom >= viewportTop + 10) {
            let chatDate = this.$refs.chatDate;

            if (el.dataset.date) {
              this.isChatDate = true;
              chatDate.innerText = el.dataset.date
            } else {
              this.isChatDate = false;
              chatDate.innerText = ''
            }
            return;
          }
        }
      },
      // Extracts timestamp for the message
      getMessageTimestamp(direction) {
        if (direction === 'DOWN') {
          if (this.messages.listOfUnreadMessages.length > 0) {
            return this.messages.listOfUnreadMessages[0].createdAt;
          } else if (this.messages.listOfReadMessages.length > 0) {
            return this.messages.listOfReadMessages[0].createdAt;
          }
        }

        if (direction === 'UP') {
          if (this.messages.listOfReadMessages.length > 0) {
            return this.messages.listOfReadMessages[this.messages.listOfReadMessages.length - 1].createdAt;
          } else if (this.messages.listOfUnreadMessages.length > 0) {
            return this.messages.listOfUnreadMessages[this.messages.listOfUnreadMessages.length - 1].createdAt;
          }
        }
      },
      // Send new message
      sendNewMessageData(e) {
        if (this.nlp === 'negative') {
          this.confirm = false;
          this.confirmModalOpen();
        }
        if (this.confirm) {
          if (e) {
            e.preventDefault();
          }

          if (this.MX_newMsg.trim() !== "") {

            // if (this.currentConversation.unreadMessageCount > 0) {
            //   this.$emit('loadAllUnreadMessages');
            // }

            this.$emit('sendMessage', this.MX_newMsg);

            this.MX_newMsg = '';
            this.newMessageSent = true;
            this.nlp = 'positive';
          }
        }
        this.isClickedOnNewMsgBtn = true;
      },
      // Format time and date
      formatDateTime(date) {
        let currentDate = new Date();
        let messageDate = new Date(date);
        let yesterday = new Date(new Date().setDate(new Date().getDate()-1));
        let sevenDaysAgo = new Date(new Date().setDate(new Date().getDate()-7));

        if (messageDate < currentDate && messageDate < yesterday && messageDate > sevenDaysAgo) {
          return date = format(messageDate, 'EEEE');
        } else if (messageDate < currentDate && messageDate < yesterday && messageDate <= sevenDaysAgo) {
          return date = format(messageDate, 'MMM dd, yyyy')
        } else if (messageDate < currentDate && messageDate <= yesterday && messageDate > sevenDaysAgo) {
          return date = this.formatRelative(subDays(currentDate, 1))
        } else {
          return date = format(messageDate, 'h:mm a')
        }
      },
      formatRelative(date) {
        let today = new Date();
        let yesterday = subDays(today, 1);

        if (yesterday.getDate() === date.getDate() && yesterday.getMonth() === date.getMonth() && yesterday.getFullYear() === date.getFullYear()) {
          return 'Yesterday at ' + format(date, 'h:mm a')
        } else {
          return 'Last ' + format(date, 'EEEE') + ' at ' + format(date, 'h:mm a')
        }
      },
      showSpiritTooltip(e) {
        e.stopPropagation();
        this.spiritTooltip = true
      },
      hideSpiritTooltip(e) {
        e.stopPropagation();
        this.spiritTooltip = false;
      },
      // // Checks if is send new message or loaded older for set scroller
      // setScroller() {
      //   if (this.newMessageSent) {
      //     console.log('nova poruka')
      //     this.scrollToBottom();
      //     this.newMessageSent = false;
      //   } else {
      //     console.log('pozovi setChatAreaScroll METHOD')
      //     this.setChatAreaScroll();
      //   }
      // },
      // // Set scroller to bottom
      // scrollToBottom() {
      //   let goToBottomButton = this.$refs.goToBottom;
      //
      //   this.scrollDirection = '';
      //   goToBottomButton.classList.remove('show-bottom-btn');
      //   this.showHideBottomBtn = !this.showHideBottomBtn;
      // },
      // jumpToBottom() {
      //   this.scrollToBottom();
      //   this.goToBottom = true;
      //   this.scrollDirection = '';
      //   this.$emit('loadAllUnreadMessages');
      //   let chatAreaContainer = this.$refs.chatAreaContainer;
      //   chatAreaContainer.scrollTo(0, 0);
      // },
      // // Show/hide button for going to the end of conversation
      // showToBottomBtn() {
      //   let chatArea = this.$refs.chatArea;
      //   let goToBottomButton = this.$refs.goToBottom;
      //   let previousScroll = 0;
      //
      //   chatArea.addEventListener('scroll', () => {
      //     let currentScroll = chatArea.scrollTop;
      //     this.scrollDirection = '';
      //     console.log('takodje pozvano')
      //
      //     if (currentScroll > previousScroll) {
      //       // Down scroll code
      //       // When user scrolled to the bottom of message container
      //       // Remove the goToBottom button
      //       if (chatArea.scrollHeight - chatArea.scrollTop === chatArea.clientHeight) {
      //         goToBottomButton.classList.remove('show-bottom-btn');
      //         this.showHideBottomBtn = false;
      //       }
      //       this.scrollDirection = 'DOWN';
      //     } else {
      //       // Up scroll code
      //       goToBottomButton.classList.add('show-bottom-btn');
      //       this.showHideBottomBtn = false;
      //       this.scrollDirection = 'UP';
      //     }
      //     previousScroll = currentScroll;
      //   });
      // },
      // // Set scroller position after load conversation
      // setChatAreaScroll() {
      //   console.log('setChatAreaScroll')
      //   let chatArea = this.$refs.chatArea;
      //   let chatAreaContainer = this.$refs.chatAreaContainer;
      //   let readMessagesArea = this.$refs.readMessagesArea;
      //   let unreadMessagesArea = this.$refs.unreadMessagesArea;
      //
      //   if (
      //     this.scrollDirection === 'UP' &&
      //     this.messageCountUp > 0
      //   ) {
      //     chatArea.scrollTo(0, (chatAreaContainer.offsetHeight - this.initialMsgContainerHeight));
      //     this.scrollDirection = '';
      //   }
      //   else if (
      //     this.scrollDirection === 'DOWN' &&
      //     this.messageCountDown > 0 &&
      //     !this.goToBottom
      //   ) {
      //     this.scrollDirection = '';
      //   }
      //   else if (chatArea) {
      //     if (this.goToBottom) {
      //       chatArea.scrollTo(0, chatAreaContainer.offsetHeight + 15);
      //     } else {
      //       if (unreadMessagesArea && unreadMessagesArea.offsetHeight >= chatArea.offsetHeight) {
      //         chatArea.scrollTo(0, readMessagesArea.offsetHeight);
      //       } else {
      //         this.initialMsgContainerHeight = 0;
      //         chatArea.scrollTo(0, chatAreaContainer.offsetHeight - chatArea.offsetHeight + 15);
      //       }
      //     }
      //   }
      // },
      // // load messages on scroll top or bottom
      // scrollApiCall(e) {
      //   console.log('pozvano')
      //   let chatAreaContainer = this.$refs.chatAreaContainer;
      //   let chatArea = this.$refs.chatArea;
      //   let unreadMessagesArea = this.$refs.unreadMessagesArea;
      //
      //   if (chatArea && chatAreaContainer) {
      //     if (chatArea.getBoundingClientRect().bottom + 30 >= chatAreaContainer.getBoundingClientRect().bottom) {
      //       this.scrollerPositionDown = true
      //     } else {
      //       this.scrollerPositionDown = false
      //     }
      //   }
      //
      //   // scroll "DOWN" Api call
      //   if ((e.target.scrollTop + e.target.offsetHeight) >= e.target.scrollHeight && !this.goToBottom) {
      //     let scrollDirection = 'DOWN';
      //
      //     if (chatAreaContainer){
      //       this.initialMsgContainerHeight = chatAreaContainer.offsetHeight;
      //     }
      //     if (unreadMessagesArea) {
      //       this.initialUnreadMsgCont = unreadMessagesArea.offsetHeight;
      //     }
      //
      //     if (!this.goToBottom) {
      //       this.loadSubsequentMessages(scrollDirection);
      //     } else {
      //       this.goToBottom = false;
      //     }
      //   // scroll "UP" Api call
      //   } else if (e.target.scrollTop === 0) {
      //     let scrollDirection = 'UP';
      //
      //     this.initialMsgContainerHeight = this.$refs.chatAreaContainer.offsetHeight;
      //
      //     this.loadSubsequentMessages(scrollDirection);
      //   }
      //   this.setDateOnScroll();
      // },
    }
  }
</script>
